/**
 * Facility create/edit modal for dashboard-specific configuration.
 */

import { useAuthSelector } from "@risalabs_frontend_org/auth-wrapper";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";

import { Select, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useSelector } from "react-redux";
import { ConfigSectionType } from "../../../../shared/enums/rbacRoles";
import { HealthCareFacilityModel } from "../../../data-model/healthFacility";
import { useAuthStatusFilter } from "../../../hooks/useAuthStatusFilter";
import { useFacilityFormData } from "../../../hooks/useFacilityFormData";
import { useHealthCareFacilities } from "../../../hooks/useHealthCareFacilities";
import { fetchClientFromFirebase } from "../../../redux/slice/clientConfiguration";
import { fetchHealthFacilityFromFirebase } from "../../../redux/slice/healthFacilitySlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { ToastMessages } from "../../toasts/toastMessages";
import {
  areClinicalTrialsSettingsComplete,
  EMPTY_CLINICAL_TRIALS_SETTINGS,
} from "./clinicalTrialsFacilitySettings";
import AddressDetailsSection from "./components/AddressDetailsSection";
import ClinicalTrialsSettingsSection from "./components/ClinicalTrialsSettingsSection";
import PrimaryDetailsSection from "./components/PrimaryDetailsSection";
import {
  addFacilityToDb as addFacilityToDbUtil,
  updateFacilityInDb as updateFacilityInDbUtil,
} from "./facilityDbOperations";
import { initialFacilityData } from "./facilityModalConstants";

const DASHBOARD_OPTIONS: { label: string; value: ConfigSectionType }[] = [
  { label: "MedOnc", value: "med_onc" },
  { label: "Pharmacy", value: "pharmacy" },
  { label: "Claims", value: "claims" },
  { label: "Clinical Trials", value: "clinical_trials" },
];

interface NewFacilityModalProps {
  data: HealthCareFacilityModel | null;
  index?: number;
  onModalClose?: (boolean) => void;
  type: "add" | "edit";
  variant?: "med_onc" | "pharmacy" | "common";
}

/**
 * Renders the facility creation and editing form.
 *
 * @param props - Modal mode, dashboard variant, and existing facility data.
 * @returns Facility modal content.
 */
const NewFacilityModal = (props: NewFacilityModalProps) => {
  const { data: clientName } = useSelector(
    (state: RootState) => state.clientConfiguration,
  );

  const [resetFormData, setResetFormData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serviceTypesInput, setServiceTypesInput] = useState("");

  const { facilityData, setFacilityData } = useFacilityFormData(
    props.data,
    initialFacilityData,
  );

  const { user } = useAuthSelector((state) => state.firebaseAuthentication);

  const dispatch = useDispatch<AppDispatch>();

  const isCommon = props.variant === "common";

  // Initialize auth status filter hook (only fetches when facilityId is provided in edit mode)
  const {
    selectedAuthStatusFilter,
    handleAuthStatusFilterChange,
    handleRemoveAuthStatusFilter,
    getAvailableAuthStatusOptions,
    authStatusFilterPlaceholder,
    hasNoOptionsAvailable,
  } = useAuthStatusFilter(
    props.type === "edit" ? props.data?.auth_status_filter : undefined,
    props.type === "edit" ? props.data?.DocId : undefined,
  );

  useEffect(() => {
    dispatch(fetchClientFromFirebase());
  }, [dispatch]);

  useEffect(() => {
    setServiceTypesInput((props.data?.service_types ?? []).join(", "));
  }, [props.data]);
  const emrOptions = useMemo(() => {
    return [
      {
        label: "Onco EMR",
        value: "oncoemr",
      },
      {
        label: "Mod Med",
        value: "modmed",
      },
    ];
  }, []);

  const categoryOptions = useMemo(() => {
    return [
      {
        label: "MedOnc",
        value: "med_onc",
      },
      {
        label: "Radiology",
        value: "radiology",
      },
      {
        label: "Urology",
        value: "urology",
      },
    ];
  }, []);

  const clientOptions = useMemo(() => {
    return clientName.map((client) => ({
      label: client.name ?? "",
      value: client.DocId ?? "",
    }));
  }, [clientName]);

  const { healthCareFacilities } = useHealthCareFacilities();

  // Filter to only internal organizations for the dropdown
  const internalFacilityOptions = useMemo(
    () =>
      healthCareFacilities.data
        ?.filter((f) => f.is_external_organization !== true)
        .map((f) => ({ value: f.DocId ?? "", label: f.Name ?? "" })) ?? [],
    [healthCareFacilities.data],
  );

  const areAllFieldsFilled = (): boolean => {
    const {
      Name,
      NPI,
      TaxID,
      PhoneNumber,
      Address,
      FhirId,
      emr_name,
      fhir_client,
      RisaPhoneNumber,
      RisaFaxNumber,
      client_id,
    } = facilityData;

    if (
      !Name ||
      !NPI ||
      !TaxID ||
      !PhoneNumber ||
      !FhirId ||
      !emr_name ||
      !fhir_client ||
      !RisaPhoneNumber ||
      !RisaFaxNumber ||
      !client_id
    ) {
      return false;
    }

    if (isCommon && !facilityData.dashboard_type) {
      return false;
    }
    if (
      facilityData.dashboard_type === "clinical_trials" &&
      !areClinicalTrialsSettingsComplete(facilityData.clinical_trials)
    ) {
      return false;
    }

    // if (is_external_organization && !organization_id) {
    //   return false;
    // }

    for (const field in Address) {
      if (!Address[field as keyof typeof Address]) {
        return false;
      }
    }

    return true;
  };

  const handleClientSelection = (selectedData) => {
    const { value } = selectedData;

    const selectedClient = clientName.find((client) => client.DocId === value);

    if (selectedClient) {
      setFacilityData((prev) => ({
        ...prev,
        client_id: value,
        NPI: selectedClient.NPI || "",
        TaxID: selectedClient.TaxID || "",
        PhoneNumber: selectedClient.PhoneNumber || "",
        FhirId: selectedClient.FhirId || "",
        Address: {
          StreetAddress: selectedClient.Address?.StreetAddress || "",
          City: selectedClient.Address?.City || "",
          State: selectedClient.Address?.State || "",
          ZIP: selectedClient.Address?.ZIP || "",
        },
        emr_name: selectedClient.emr_name || "",
        fhir_client: selectedClient.fhir_client || "",
        RisaFaxNumber: selectedClient.RisaFaxNumber || "",
        RisaPhoneNumber: selectedClient.RisaPhoneNumber || "",
        category: selectedClient.category || "",
      }));
    }
  };

  /**
   * Applies the selected dashboard and enforces external tenancy for Clinical Trials.
   *
   * @param selectedData - Select option containing the dashboard family
   * @returns Nothing
   */
  const handleDashboardTypeChange = (selectedData: { value: string }): void => {
    const dashboardType = selectedData.value as ConfigSectionType;
    setFacilityData((prev) => ({
      ...prev,
      dashboard_type: dashboardType,
      is_external_organization:
        dashboardType === "clinical_trials"
          ? true
          : prev.is_external_organization,
      clinical_trials:
        dashboardType === "clinical_trials"
          ? (prev.clinical_trials ?? EMPTY_CLINICAL_TRIALS_SETTINGS)
          : prev.clinical_trials,
    }));
  };

  /**
   * Replaces the nested Clinical Trials settings in facility form state.
   *
   * @param settings - Complete nested settings emitted by the CT fieldset
   * @returns Nothing
   */
  const handleClinicalTrialsSettingsChange = (
    settings: NonNullable<HealthCareFacilityModel["clinical_trials"]>,
  ): void => {
    setFacilityData((prev) => ({ ...prev, clinical_trials: settings }));
  };

  /**
   * Applies a primary facility-form field update to local state.
   * Converts `reasons_min_count` to a number and clears it when the input is empty.
   *
   * @param data - Changed input name and value.
   * @returns Nothing.
   */
  const handleInputChangeForPrimaryDetails = (data) => {
    const { name, value } = data;
    setFacilityData((prev) => ({
      ...prev,
      [name]:
        name === "reasons_min_count"
          ? value === ""
            ? undefined
            : Number(value)
          : value,
    }));
  };

  const handleServiceTypesChange = (data: { value: string }) => {
    setServiceTypesInput(data.value);
  };

  const handleChangeForAddressDetails = (data) => {
    const { name, value } = data;
    setFacilityData((prev) => ({
      ...prev,
      Address: {
        ...prev.Address,
        [name]: value,
      },
    }));
  };

  const handleOnClose = () => {
    props.onModalClose && props.onModalClose(false);
  };

  const addFacilityToDb = async () => {
    try {
      setLoading(true);

      await addFacilityToDbUtil({
        facilityData,
        serviceTypesInput,
        userEmail: user?.email,
      });

      dispatch(fetchHealthFacilityFromFirebase());
      toast.success(ToastMessages.FACILITY_ADDED_SUCCESS);
      handleOnClose();
    } catch (error) {
      console.error("Error adding facility to firebase", error);
      toast.error(ToastMessages.FACILITY_ADDED_FAILURE);
    } finally {
      setLoading(false);
    }
  };

  const updateFacilityInDb = async () => {
    setLoading(true);
    try {
      await updateFacilityInDbUtil({
        facilityId: props.data?.DocId || "",
        existingData: props.data,
        facilityData,
        selectedAuthStatusFilter,
        serviceTypesInput,
        userEmail: user?.email,
      });
      dispatch(fetchHealthFacilityFromFirebase());
      toast.success(ToastMessages.FACILITY_UPDATED_SUCCESS);
      handleOnClose();
    } catch (error) {
      console.error("Error updating facility to firebase", error);
      toast.error(ToastMessages.FACILITY_UPDATED_FAILURE);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (resetFormData) {
      setResetFormData(false);
    }
  }, [facilityData]);

  return (
    <Modal
      dialogId={"new-facility-modal"}
      onSave={props.type === "add" ? addFacilityToDb : updateFacilityInDb}
      title={props.type === "add" ? "Onboard New Facility" : "Edit Facility"}
      saveButtonText={loading ? "Saving..." : "Save"}
      cancelText={"Cancel"}
      heightPercentage={70}
      disableSave={!areAllFieldsFilled() || loading}
      onClose={handleOnClose}
    >
      <div className="facility-form-container">
        <form>
          {isCommon && (
            <div className="mb-3">
              <Select
                id={"dashboardType"}
                label={"Dashboard"}
                placeholder={"Select a dashboard ..."}
                required
                onOptionChange={handleDashboardTypeChange}
                error={
                  !facilityData.dashboard_type
                    ? "Please select a dashboard"
                    : ""
                }
                defaultValue={facilityData.dashboard_type}
                options={DASHBOARD_OPTIONS}
              />
            </div>
          )}
          {facilityData.dashboard_type === "clinical_trials" && (
            <ClinicalTrialsSettingsSection
              settings={
                facilityData.clinical_trials ?? EMPTY_CLINICAL_TRIALS_SETTINGS
              }
              resetFormData={resetFormData}
              onChange={handleClinicalTrialsSettingsChange}
            />
          )}
          <PrimaryDetailsSection
            facilityData={facilityData}
            resetFormData={resetFormData}
            handleClientSelection={handleClientSelection}
            handleInputChangeForPrimaryDetails={
              handleInputChangeForPrimaryDetails
            }
            clientOptions={clientOptions}
            emrOptions={emrOptions}
            type={props.type}
            selectedAuthStatusFilter={selectedAuthStatusFilter}
            handleAuthStatusFilterChange={handleAuthStatusFilterChange}
            handleRemoveAuthStatusFilter={handleRemoveAuthStatusFilter}
            getAvailableAuthStatusOptions={getAvailableAuthStatusOptions}
            authStatusFilterPlaceholder={authStatusFilterPlaceholder}
            hasNoOptionsAvailable={hasNoOptionsAvailable}
            facilityOptions={internalFacilityOptions}
            categoryOptions={categoryOptions}
            serviceTypesValue={serviceTypesInput}
            handleServiceTypesChange={handleServiceTypesChange}
          />
          <AddressDetailsSection
            facilityData={facilityData}
            resetFormData={resetFormData}
            handleChangeForAddressDetails={handleChangeForAddressDetails}
          />
        </form>
      </div>
    </Modal>
  );
};

export default NewFacilityModal;
